import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AddressFieldName } from '@as-react/common-types';
import { Box, LinkButton, ButtonAlpha, TextAlpha, Stack } from '@yonderland/andes-react';
import { useEffect, useMemo, Fragment } from 'react';
import { useFormContext, useWatch, get } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DELAY } from '../../../constants/GeneralConstants';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { mapWithAssert } from '../../../util/ArrayUtil';
import { generateFieldName } from './addressFields.utils';
export const AddressCard = ({ children, addressFields, isEditEnabled, setIsEditEnabled, onSaveClick, prefix, country, }) => {
    const allAddressFields = mapWithAssert([
        AddressFieldName.POSTCODE,
        AddressFieldName.HOUSE_NUMBER,
        AddressFieldName.HOUSE_NUMBER_ADDITION,
        AddressFieldName.CITY,
        AddressFieldName.ADDRESS,
        AddressFieldName.ADDRESS_LINE_2,
        AddressFieldName.ADDRESS_LINE_3,
        AddressFieldName.ADDRESS_LINE_4,
        AddressFieldName.COUNTY,
    ], name => generateFieldName(name, prefix));
    const { t } = useTranslation(I18N_NAMESPACES.general);
    const { setValue, formState } = useFormContext();
    const addressValues = useWatch({ name: addressFields });
    const [postCode, houseNumber, houseNumberAddition, city, address, address2, address3, address4, county] = useWatch({
        name: allAddressFields,
    });
    const defaultValues = useMemo(() => addressFields.map((name, i) => ({ field: name, value: addressValues[i] })), [isEditEnabled]);
    const restoreDefaultValues = () => defaultValues.map(({ field, value }) => setValue(field, value, { shouldValidate: true }));
    const hasInvalidFields = () => addressFields.some(field => Boolean(get(formState.errors, field)));
    const handleCancelClick = () => {
        restoreDefaultValues();
        setTimeout(() => !hasInvalidFields() && setIsEditEnabled(false), DELAY.DELAY_0);
    };
    const handleSaveClick = () => {
        if (hasInvalidFields())
            return;
        setIsEditEnabled(false);
        onSaveClick === null || onSaveClick === void 0 ? void 0 : onSaveClick(!addressValues.some(Boolean));
    };
    const getAddressLinesByCountry = () => {
        switch (country) {
            case 'UK':
                return [
                    [address, address2]
                        .filter(Boolean)
                        .join(', ')
                        .concat((address || address2) && (address3 || address4) ? ',' : ''),
                    [address3, address4].filter(Boolean).join(', '),
                    [postCode, city]
                        .filter(Boolean)
                        .join(' ')
                        .concat(county ? `${postCode || city ? ', ' : ''}${county}` : ''),
                ].filter(Boolean);
            case 'NL':
                return [
                    [address, houseNumber]
                        .filter(Boolean)
                        .join(' ')
                        .concat(houseNumberAddition ? `${address || houseNumber ? ', ' : ''}${houseNumberAddition}` : ''),
                    [postCode, city].filter(Boolean).join(' '),
                ].filter(Boolean);
            default:
                return [];
        }
    };
    useEffect(() => {
        hasInvalidFields() && setIsEditEnabled(true);
    }, [formState.isSubmitting]);
    return (_jsx(Box, { padding: "4", backgroundColor: "quiet", testId: "address-card", children: isEditEnabled ? (_jsxs(Stack, { direction: "col", gap: "6", children: [children, _jsxs(Stack, { gap: "3", children: [_jsx(ButtonAlpha, { theme: "branded", onClick: handleSaveClick, children: t('address.card.save') }), _jsx(LinkButton, { theme: "branded", onClick: handleCancelClick, children: t('address.card.cancel') })] })] })) : (_jsxs(Stack, { gap: "4", justify: "between", wrap: "nowrap", children: [_jsx(Box, { children: _jsx(TextAlpha, { children: getAddressLinesByCountry().map((line, index, arr) => (_jsxs(Fragment, { children: [line, arr.length - 1 === index ? '' : _jsx("br", {})] }, `${line}_${index}`))) }) }), _jsx(Box, { children: _jsx(LinkButton, { testId: "address-card-edit-button", onClick: () => setIsEditEnabled(true), children: t('address.card.edit') }) })] })) }));
};
